import { C, SERIF, SANS, PAGE_W, PAGE_H } from "./theme";
import { ReportHeader, ReportFooter, Eyebrow, Label, LogoDark } from "./ReportChrome";
import { gbp, esc } from "./reportUtils";

const PILLARS = [
  ["Listings and pricing", "Live on 16+ platforms, repriced hourly."],
  ["Guests and security", "ID-verified guests, 7-day messaging, damage cover."],
  ["Cleaning", "Professional turnover after every stay, paid by guests."],
  ["Payments", "Paid within 48 hours of each check-in."],
];

const STEPS = [
  ["Today", "Valuation", "Done — happy to talk it through."],
  ["Days 3–5", "Set-up", "Photos, listing, compliance."],
  ["Day 7", "Live", "On Airbnb, Booking.com and more."],
  ["First stay", "First payout", "Within 48 hours of check-in."],
];

const TRUST = [["5.0", "Google, 90+ reviews"], ["4.76", "Airbnb, 1,000+ reviews"], ["35,000+", "Stays since 2019"]];

export default function ServicePage({ pageRef, host, address, cityName, mgmtFee, offer, contact, netTotal, bedrooms, guests, baseRate, occupancy, vatRate, platformFee, validTo, refCode }) {
  const c = contact || {};
  return (
    <div ref={pageRef} style={{ width: PAGE_W, height: PAGE_H, background: "#fff", color: C.evergreen, position: "relative", overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <ReportHeader host={host} address={address} cityName={cityName} />
      <div style={{ padding: "32px 64px 0", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Eyebrow>Our service</Eyebrow>
        <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 28, lineHeight: 1.1, letterSpacing: "-0.01em", margin: "8px 0 10px" }}>
          Everything handled, end to end
        </h2>
        <p style={{ fontFamily: SANS, fontSize: 12, lineHeight: 1.6, color: C.muted, maxWidth: 480, margin: 0 }}>
          One local team handles it all. You just keep the dates you want for yourself.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", marginTop: 24, rowGap: 14, columnGap: 32 }}>
          {PILLARS.map(([title, body]) => (
            <div key={title} style={{ padding: "14px 0 0", borderTop: `1px solid ${C.line}` }}>
              <h4 style={{ fontFamily: SANS, fontWeight: 600, fontSize: 11.5, marginBottom: 5 }}>{title}</h4>
              <p style={{ fontFamily: SANS, fontSize: 10, lineHeight: 1.5, color: C.muted, margin: 0 }}>{body}</p>
            </div>
          ))}
        </div>

        <div style={{ background: C.evergreen, color: "#fff", borderRadius: 14, marginTop: 28, padding: "22px 26px", display: "grid", gridTemplateColumns: "1fr 1.1fr", gap: 24, alignItems: "center" }}>
          <div>
            <Label color={C.tea}>Your fee</Label>
            <div style={{ fontFamily: SERIF, fontSize: 38, lineHeight: 1.3, marginTop: 6, paddingBottom: 6, display: "flex", alignItems: "baseline" }}>{mgmtFee}%<span style={{ fontSize: 16 }}> + VAT</span></div>
            <div style={{ fontFamily: SANS, fontSize: 10, color: "rgba(255,255,255,0.7)", marginTop: 8, lineHeight: 1.45 }}>Per booking — empty nights cost nothing.</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, fontFamily: SANS, fontSize: 10.5, color: "rgba(255,255,255,0.88)" }}>
            <div>No monthly retainer</div>
            <div>No lock-in, 30 days' notice</div>
            <div>Block dates for your own use, free</div>
          </div>
        </div>

        <div style={{ marginTop: 28 }}>
          <Label>From here to live in seven days</Label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 20, marginTop: 14 }}>
            {STEPS.map(([w, title, body], i) => (
              <div key={title}>
                <div style={{ fontFamily: SERIF, fontSize: 20, color: C.copper, lineHeight: 1.3 }}>0{i + 1}</div>
                <div style={{ fontFamily: SANS, fontSize: 8, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: C.faint, margin: "10px 0 5px" }}>{w}</div>
                <h4 style={{ fontFamily: SANS, fontWeight: 600, fontSize: 11, marginBottom: 4 }}>{title}</h4>
                <p style={{ fontFamily: SANS, fontSize: 9.5, lineHeight: 1.45, color: C.muted, margin: 0 }}>{body}</p>
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 32, paddingTop: 24, borderTop: `1px solid ${C.line}` }}>
          <Eyebrow>Next steps</Eyebrow>
          <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: 24, lineHeight: 1.15, maxWidth: 560, margin: "8px 0 18px" }}>
            Ready to get started? Let us know.
          </h2>
          <div style={{ background: C.evergreen, color: "#fff", borderRadius: 14, padding: "22px 26px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, alignItems: "center" }}>
            <div>
              <Label color={C.tea}>Your point of contact</Label>
              <div style={{ fontFamily: SERIF, fontSize: 22, marginTop: 6, lineHeight: 1.3, paddingBottom: 4 }}>Ollie Whitehouse</div>
              <div style={{ fontFamily: SANS, fontSize: 10, color: "rgba(255,255,255,0.7)", marginTop: 4 }}>Managing Director</div>
            </div>
            <div style={{ fontFamily: SANS, fontSize: 11, lineHeight: 1.9, color: "rgba(255,255,255,0.85)" }}>
              {c.email && <>{esc(c.email)}<br /></>}
              {c.phone && <>{esc(c.phone)}<br /></>}
              truestays.co.uk
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", marginTop: 24, borderTop: `1px solid ${C.line}`, borderBottom: `1px solid ${C.line}` }}>
          {TRUST.map(([val, desc], i) => (
            <div key={val} style={{ padding: "16px 18px 16px 0", borderLeft: i ? `1px solid ${C.line}` : "none", paddingLeft: i ? 18 : 0 }}>
              <div style={{ fontFamily: SERIF, fontSize: 22, lineHeight: 1.3, paddingBottom: 4 }}>{val}</div>
              <div style={{ fontFamily: SANS, fontSize: 9.5, color: C.muted, marginTop: 5 }}>{desc}</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: "auto", paddingTop: 18, fontFamily: SANS, fontSize: 8, lineHeight: 1.6, color: C.faint }}>
          <b style={{ color: C.muted }}>How this estimate works.</b> Based on comparable short-let properties in {cityName}, live pricing data and the details provided. Net income is after a {mgmtFee}% management fee plus {vatRate}% VAT, and {platformFee}% platform fees. Cleaning is paid by guests. Results are not guaranteed. Valid until {validTo}. Truestays Group Ltd.
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 18, paddingTop: 10 }}>
          <img src="https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/24682ceeb_TruestaysLogo.png" alt="Truestays" style={{ height: 18 }} crossOrigin="anonymous" />
          <div style={{ fontFamily: SANS, fontSize: 9, color: C.faint, textAlign: "right", lineHeight: 1.5 }}>Ref {refCode}<br />5 / 5</div>
        </div>
      </div>
    </div>
  );
}